import React, { useState, useEffect, useRef } from 'react';
import Calling from './api/Calling';
import img1 from './Image/NLogo.svg'

const Main_Url = "/live_main";

const Client = () => {
    // State management
    const [loader, setLoader] = useState(false);
    const [videos, setVideos] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [webPosition, setWebPosition] = useState(0);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isSmartphone, setIsSmartphone] = useState(false);
    const [showAppDialog, setShowAppDialog] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const [showDownloadBox, setShowDownloadBox] = useState(false);

    // Modified: Always show landing box first on all devices
    const [showLandingBox, setShowLandingBox] = useState(true);
    const [contentAvailable, setContentAvailable] = useState(null); // Start with null instead of true

    // Refs for video elements
    const videoRefs = useRef([]);
    const containerRef = useRef(null);

    // Mock toast service
    const toast = {
        error: (msg) => console.error(msg)
    };

    // Check if device is a smartphone (not tablet or desktop)
    useEffect(() => {
        const checkSmartphone = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;

            // First check if it's a mobile device but not a tablet
            // Note: This more precise regex excludes tablets while detecting phones
            const phoneRegex = /(?!.*tablet)(?=.*mobile)|Android.*(?=.*mobile)|iPhone/i;
            const isPhone = phoneRegex.test(userAgent);

            // More specific tablet detection
            const tabletRegex = /iPad|tablet|Tablet|PlayBook|Android(?!.*mobile)/i;
            const isTablet = tabletRegex.test(userAgent);

            // Screen dimensions check - most phones are narrower than tablets
            const isPhoneWidth = window.innerWidth < 640;

            // Return true ONLY for smartphones - exclude tablets explicitly
            return isPhone && !isTablet && isPhoneWidth;
        };

        const smartphone = checkSmartphone();
        setIsSmartphone(smartphone);

        // MODIFIED: Don't set content as available yet - always show landing box first
        // Only show app dialog for non-smartphones
        if (!smartphone) {
            setShowAppDialog(true);
        }

        // IMPORTANT: Don't fetch videos yet - wait for user to click "Continue"
    }, []);

    // Handle showing download box based on webPosition
    useEffect(() => {
        if (contentAvailable === true) {
            if (webPosition > 0) {
                // Set a timeout only if webPosition > 0
                const timer = setTimeout(() => {
                    console.log("Showing download box after", webPosition, "seconds");
                    setShowDownloadBox(true);
                }, webPosition * 1000);

                return () => clearTimeout(timer);
            } else {
                // Show immediately if webPosition is 0
                console.log("Showing download box immediately");
                setShowDownloadBox(true);
            }
        }
    }, [webPosition, contentAvailable]);


    // Listen for resize events to update device detection
    useEffect(() => {
        const handleResize = () => {
            // Re-check on resize with stricter width conditions
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const phoneRegex = /(?!.*tablet)(?=.*mobile)|Android.*(?=.*mobile)|iPhone/i;
            const isPhone = phoneRegex.test(userAgent);
            const tabletRegex = /iPad|tablet|Tablet|PlayBook|Android(?!.*mobile)/i;
            const isTablet = tabletRegex.test(userAgent);
            const isPhoneWidth = window.innerWidth < 640;

            const smartphone = isPhone && !isTablet && isPhoneWidth;

            if (isSmartphone !== smartphone) {
                setIsSmartphone(smartphone);
                setShowAppDialog(!smartphone);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSmartphone]);

    // Track user interactions to enable autoplay
    useEffect(() => {
        // Only set up interaction handlers if content is available
        if (contentAvailable !== true) return;

        const handleUserInteraction = () => {
            setUserInteracted(true);

            // Try to play the current video
            if (videoRefs.current[currentVideoIndex]) {
                const playPromise = videoRefs.current[currentVideoIndex].play();
                if (playPromise !== undefined) {
                    playPromise.catch(e => console.log("Play after interaction failed:", e));
                }
            }

            // Remove the event listeners once user has interacted
            document.removeEventListener('touchstart', handleUserInteraction);
            document.removeEventListener('click', handleUserInteraction);
        };

        document.addEventListener('touchstart', handleUserInteraction);
        document.addEventListener('click', handleUserInteraction);

        return () => {
            document.removeEventListener('touchstart', handleUserInteraction);
            document.removeEventListener('click', handleUserInteraction);
        };
    }, [currentVideoIndex, contentAvailable]);

    // MODIFIED: Only fetch videos when content is available and landing box is closed
    useEffect(() => {
        if (isSmartphone && contentAvailable === true && !showLandingBox) {
            if (videos.length === 0) {
                // Initial fetch when content becomes available (after clicking Continue)
                fetchVideos();
            } else if (currentPage > 1) {
                // Fetch more pages for infinite scroll
                fetchVideos();
            }
        }
    }, [currentPage, isSmartphone, contentAvailable, showLandingBox, videos.length]);

    // Handle intersection observer for infinite scrolling
    useEffect(() => {
        if (!videos.length || !isSmartphone || contentAvailable !== true) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Get the index from the video element's data attribute
                        const index = parseInt(entry.target.dataset.index);
                        setCurrentVideoIndex(index);

                        // Play the current video and pause others
                        videoRefs.current.forEach((videoRef, i) => {
                            if (videoRef && i === index) {
                                videoRef.muted = true; // Mobile browsers require muted for autoplay

                                // Force load the video if not already loaded
                                if (videoRef.readyState === 0) {
                                    videoRef.load();
                                }

                                const playPromise = videoRef.play();
                                if (playPromise !== undefined) {
                                    playPromise.catch(e => {
                                        console.log("Autoplay prevented:", e);

                                        // If user has already interacted, we can try again
                                        if (userInteracted) {
                                            setTimeout(() => {
                                                videoRef.play().catch(err =>
                                                    console.log("Retry play failed:", err)
                                                );
                                            }, 300);
                                        }
                                    });
                                }

                                // If user has interacted before, we can unmute
                                if (userInteracted) {
                                    videoRef.muted = false;
                                }
                            } else if (videoRef) {
                                videoRef.pause();
                            }
                        });

                        // Load more videos if we're near the end
                        if (index >= videos.length - 2 && currentPage < totalPages) {
                            setCurrentPage(prev => prev + 1);
                        }
                    }
                });
            },
            { threshold: 0.7 } // 70% of the video must be visible
        );

        // Observe all video containers
        videoRefs.current.forEach((_, index) => {
            const videoContainer = document.querySelector(`[data-container="${index}"]`);
            if (videoContainer) {
                observer.observe(videoContainer);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, [videos, videoRefs.current.length, isSmartphone, userInteracted, contentAvailable, currentPage, totalPages]);

    // Ensure first video plays on initial load
    useEffect(() => {
        if (videos.length > 0 && isSmartphone && videoRefs.current[0] && contentAvailable === true) {
            // Set explicit attributes
            const firstVideo = videoRefs.current[0];
            firstVideo.muted = true;
            firstVideo.playsInline = true;
            firstVideo.setAttribute('playsinline', '');
            firstVideo.setAttribute('webkit-playsinline', '');

            // Make sure it's loaded
            if (firstVideo.readyState === 0) {
                firstVideo.load();
            }

            // Try to play with a slight delay to ensure DOM is ready
            setTimeout(() => {
                firstVideo.play().catch(e => {
                    console.log("Initial autoplay prevented:", e);
                    // We'll rely on the user interaction handler to play it
                });
            }, 100);
        }
    }, [videos.length, isSmartphone, contentAvailable]);

    const fetchVideos = async () => {
        try {
            setLoader(true);
            setError(null);

            const response = await Calling.getVideoClient(currentPage, limit);

            if (response.status) {
                setVideos(prev =>
                    currentPage === 1 ? response.data : [...prev, ...response.data]
                );
                setTotalPages(response.totalPage);

                // Ensure webPosition is set correctly from response
                if (response.webPosition !== undefined) {
                    setWebPosition(response.webPosition);
                }
            } else {
                setError(response.message || 'Failed to fetch videos');
                console.error('Failed to fetch videos:', response.message);
                toast.error(response.message || 'Failed to load videos');
            }

        } catch (error) {
            setError('Error connecting to server');
            console.error('Error fetching videos:', error);
            toast.error('Connection error - Please try again later');
        } finally {
            setLoader(false);
        }
    };

    // Handle user choice on the landing box
    const handleUserChoice = (choice) => {
        setShowLandingBox(false);
        if (choice === 'continue') {
            setContentAvailable(true);
            // No need to call fetchVideos here - it will be triggered by the useEffect
        } else {
            setContentAvailable(false);
        }
    };

    // Landing Box Component
    const LandingBox = () => (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4 text-center">Welcome</h2>
                <p className="mb-6 text-center">
                    Do you want to view the content?
                </p>
                <div className="flex flex-col space-y-4">
                    <button
                        className="bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg flex items-center justify-center"
                        onClick={() => handleUserChoice('continue')}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );

    // App download dialog component
    const AppDownloadDialog = () => (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-bold mb-4 text-center">Get Our Mobile App</h2>
                <p className="mb-6 text-center">
                    This content is only available on smartphones. Tablets and desktop devices are not supported.
                </p>
                <div className="flex flex-col space-y-4">
                    <a
                        href="#"
                        className="bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg flex items-center justify-center"
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.href = Main_Url;
                        }}
                    >
                        <span className="mr-2">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M3.609 1.814L13.792 12l-10.183 10.186c-0.287-0.504-0.451-1.089-0.451-1.714V3.528c0-0.625 0.164-1.21 0.451-1.714zM14.523 12.731L5.909 21.186l11.572-6.436-2.958-2.019zM5.909 2.814l8.614 8.455 2.958-2.019-11.572-6.436zM21.129 11.465c0-0.343-0.192-0.673-0.497-0.826l-3.566-1.925L14.523 12l2.543 3.286 3.566-1.925c0.305-0.154 0.497-0.483 0.497-0.826z" />
                            </svg>
                        </span>
                        Download from Google Play
                    </a>
                    <div className="text-center text-sm text-gray-400 mt-4">
                        For the best experience, please use a smartphone
                    </div>
                </div>
            </div>
        </div>
    );

    // Not Available Component
    const NotAvailableMessage = () => (
        <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
                <h2 className="text-xl font-bold mb-4">Content Not Available</h2>
                <p>The content is currently not available.</p>
                <button
                    className="mt-6 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg"
                    onClick={() => {
                        setShowLandingBox(true);
                        setContentAvailable(null);
                    }}
                >
                    Try Again
                </button>
            </div>
        </div>
    );

    // New Download Box Component that appears after webPosition seconds
    const DownloadAppBox = () => (
        <div className="fixed bottom-0 left-0 right-0 bg-black p-4 z-50 flex shadow-lg justify-center items-center h-full">
            <div className="flex flex-col items-center text-center">
                <div className="flex justify-center items-center mb-2">
                    <img src={img1} alt="Logo" className="h-20" />
                </div>
                <p className="my-4 text-sm">Play More Video/अधिक वीडियो देखिए</p>
                <button
                    className="bg-main-blue hover:bg-main-blue/80 text-white py-2 px-4 rounded-lg w-full"
                    onClick={() => {
                        window.location.href = Main_Url;
                    }}
                >
                    Watch Full Video In App /ऐप में पूरा वीडियो देखें
                </button>
            </div>
        </div>
    );

    return (
        <div className="h-screen w-screen flex flex-col bg-black text-white overflow-hidden">
            {/* Always show landing box first for all devices */}
            {showLandingBox && <LandingBox />}

            {/* Show app dialog for non-smartphones (after landing box is closed) */}
            {!showLandingBox && showAppDialog && !isSmartphone && <AppDownloadDialog />}

            {/* Show the download box if webPosition seconds have passed and content is available */}
            {showDownloadBox && isSmartphone && contentAvailable === true && <DownloadAppBox />}

            {isSmartphone ? (
                <>
                    {/* Only show content if landing box is closed and continue was clicked */}
                    {!showLandingBox && (
                        <>
                            {contentAvailable === false ? (
                                <NotAvailableMessage />
                            ) : contentAvailable === true ? (
                                // Show loader or error or videos if user chose to continue
                                <>
                                    {loader && videos.length === 0 ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                                        </div>
                                    ) : error ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <div className="text-red-500">{error}</div>
                                        </div>
                                    ) : (
                                        <div
                                            className="flex-1 h-full w-full overflow-hidden"
                                            onClick={() => {
                                                // Enable audio on user interaction
                                                setUserInteracted(true);
                                                const currentVideo = videoRefs.current[currentVideoIndex];
                                                if (currentVideo) {
                                                    currentVideo.muted = false;
                                                    currentVideo.play().catch(e => console.log("Play on click failed:", e));
                                                }
                                            }}
                                        >
                                            <VideoFeed videos={videos} />

                                            {loader && videos.length > 0 && (
                                                <div className="absolute bottom-0 left-0 right-0 h-16 flex items-center justify-center z-10">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : null}
                        </>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default Client;

// VideoFeed component remains the same
const VideoFeed = ({ videos }) => {
    const [activeVideoIndex, setActiveVideoIndex] = useState(0);
    const videoRefs = useRef([]);
    const containerRef = useRef(null);

    const setVideoRef = (el, index) => {
        if (el) {
            videoRefs.current[index] = el;
        }
    };

    // Handle scroll to play videos in view
    useEffect(() => {
        const handleScroll = () => {
            const containers = document.querySelectorAll('[data-container]');

            containers.forEach((container) => {
                const index = parseInt(container.dataset.index);
                const rect = container.getBoundingClientRect();
                const videoEl = videoRefs.current[index];

                if (!videoEl) return;

                // Calculate how centered the video is in the viewport
                const viewportHeight = window.innerHeight;
                const videoCenter = rect.top + rect.height / 2;
                const viewportCenter = viewportHeight / 2;
                const distanceFromCenter = Math.abs(videoCenter - viewportCenter);
                const percentVisible = (viewportHeight - distanceFromCenter * 2) / viewportHeight;

                // Consider a video "active" if it's more than 50% centered in the viewport
                if (percentVisible > 0.5) {
                    if (videoEl.paused) {
                        // Promise to handle autoplay issues in some browsers
                        const playPromise = videoEl.play();

                        if (playPromise !== undefined) {
                            playPromise.catch(error => {
                                // Auto-play was prevented, try playing muted
                                if (error.name === "NotAllowedError") {
                                    videoEl.muted = true;
                                    videoEl.play().catch(e => console.log("Couldn't play even when muted:", e));
                                }
                            });
                        }

                        setActiveVideoIndex(index);
                    }
                } else {
                    if (!videoEl.paused) {
                        videoEl.pause();
                    }
                }
            });
        };

        // Force all videos to load and initialize
        const initializeVideos = () => {
            videoRefs.current.forEach((videoEl, index) => {
                if (videoEl) {
                    // Make sure the first video plays on load
                    if (index === 0) {
                        const playPromise = videoEl.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(e => console.log("Couldn't autoplay first video:", e));
                        }
                    } else {
                        // Pause other videos initially
                        videoEl.pause();
                        // Preload all videos
                        videoEl.load();
                    }
                }
            });

            // Run the scroll handler to play the correct video
            handleScroll();
        };

        // Run initialization after a short delay to ensure refs are set
        setTimeout(initializeVideos, 100);

        // Add event listener for scroll
        window.addEventListener('scroll', handleScroll);
        // Also check on intersection changes
        const observer = new IntersectionObserver(handleScroll, {
            threshold: [0.1, 0.5, 0.9]
        });

        document.querySelectorAll('[data-container]').forEach(container => {
            observer.observe(container);
        });

        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            className="snap-y snap-mandatory h-full w-full overflow-y-scroll"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={() => setTimeout(() => containerRef.current && containerRef.current.dispatchEvent(new CustomEvent('scrollend')), 50)}
        >
            {videos.map((video, index) => (
                <div
                    key={video._id}
                    data-container={index}
                    data-index={index}
                    className="h-full w-full snap-start snap-always relative"
                >
                    <video
                        ref={(el) => setVideoRef(el, index)}
                        data-index={index}
                        src={video.video}
                        className="absolute inset-0 w-full h-full object-cover"
                        loop
                        muted
                        playsInline
                        disablePictureInPicture
                        disableRemotePlayback
                        controlsList="nodownload noplaybackrate nofullscreen"
                        preload="auto"
                        onCanPlay={(e) => {
                            if (index === activeVideoIndex) {
                                e.target.play().catch(err => console.log("Play error:", err));
                            }
                        }}
                    />
                </div>
            ))}
        </div>
    );
};